import { AudioCue } from '../types/audio';

export interface DroneParams {
  fundamentalFreq1: number;
  fundamentalFreq2: number;
  overtoneFreq1: number;
  overtoneFreq2: number;
  filterCutoff: number;
  filterQ: number;
  droneGain: number;
  overtoneGain: number;
}

/**
 * Detuned dual-oscillator binaural drone synthesizer.
 * Generates fundamental binaural beating (40.0Hz / 43.2Hz) producing a ~3.2Hz theta/delta beat,
 * along with harmonic overtones and resonant low-pass filtering.
 */
export class DroneSynth {
  private ctx: AudioContext;
  private outputNode: GainNode;
  private filterNode: BiquadFilterNode;
  private droneGainNode: GainNode;
  private overtoneGainNode: GainNode;

  // Primary binaural pair
  private osc1: OscillatorNode | null = null;
  private osc2: OscillatorNode | null = null;
  private panner1: StereoPannerNode | null = null;
  private panner2: StereoPannerNode | null = null;

  // Harmonic overtones
  private overtoneOsc1: OscillatorNode | null = null;
  private overtoneOsc2: OscillatorNode | null = null;

  private isStarted = false;

  // Baseline tuning
  private baseFreq1 = 40.0;
  private baseFreq2 = 43.2;

  constructor(ctx: AudioContext) {
    this.ctx = ctx;

    // Master output for drone module
    this.outputNode = this.ctx.createGain();
    this.outputNode.gain.setValueAtTime(0.7, this.ctx.currentTime);

    // Resonant low-pass filter
    this.filterNode = this.ctx.createBiquadFilter();
    this.filterNode.type = 'lowpass';
    this.filterNode.frequency.setValueAtTime(160, this.ctx.currentTime);
    this.filterNode.Q.setValueAtTime(3.0, this.ctx.currentTime);

    // Sub-mix gains
    this.droneGainNode = this.ctx.createGain();
    this.droneGainNode.gain.setValueAtTime(0.5, this.ctx.currentTime);

    this.overtoneGainNode = this.ctx.createGain();
    this.overtoneGainNode.gain.setValueAtTime(0.15, this.ctx.currentTime);

    // Routing
    this.droneGainNode.connect(this.filterNode);
    this.overtoneGainNode.connect(this.filterNode);
    this.filterNode.connect(this.outputNode);
  }

  public connect(destination: AudioNode): void {
    this.outputNode.connect(destination);
  }

  public getOutputNode(): GainNode {
    return this.outputNode;
  }

  public getFilterNode(): BiquadFilterNode {
    return this.filterNode;
  }

  public getDroneGainNode(): GainNode {
    return this.droneGainNode;
  }

  public getOvertoneGainNode(): GainNode {
    return this.overtoneGainNode;
  }

  public getOscillators(): {
    osc1: OscillatorNode | null;
    osc2: OscillatorNode | null;
    overtoneOsc1: OscillatorNode | null;
    overtoneOsc2: OscillatorNode | null;
  } {
    return {
      osc1: this.osc1,
      osc2: this.osc2,
      overtoneOsc1: this.overtoneOsc1,
      overtoneOsc2: this.overtoneOsc2,
    };
  }

  public start(): void {
    if (this.isStarted) return;
    this.isStarted = true;

    const t = this.ctx.currentTime;

    // Osc 1: 40.0 Hz Triangle wave
    this.osc1 = this.ctx.createOscillator();
    this.osc1.type = 'triangle';
    this.osc1.frequency.setValueAtTime(this.baseFreq1, t);

    // Osc 2: 43.2 Hz Sine wave
    this.osc2 = this.ctx.createOscillator();
    this.osc2.type = 'sine';
    this.osc2.frequency.setValueAtTime(this.baseFreq2, t);

    // Binaural Stereo Panning (Left / Right spatial separation)
    if (typeof this.ctx.createStereoPanner === 'function') {
      this.panner1 = this.ctx.createStereoPanner();
      this.panner1.pan.setValueAtTime(-0.6, t);
      this.osc1.connect(this.panner1);
      this.panner1.connect(this.droneGainNode);

      this.panner2 = this.ctx.createStereoPanner();
      this.panner2.pan.setValueAtTime(0.6, t);
      this.osc2.connect(this.panner2);
      this.panner2.connect(this.droneGainNode);
    } else {
      this.osc1.connect(this.droneGainNode);
      this.osc2.connect(this.droneGainNode);
    }

    // Overtone 1: 2nd harmonic (80.0 Hz) Sine
    this.overtoneOsc1 = this.ctx.createOscillator();
    this.overtoneOsc1.type = 'sine';
    this.overtoneOsc1.frequency.setValueAtTime(this.baseFreq1 * 2, t);
    this.overtoneOsc1.connect(this.overtoneGainNode);

    // Overtone 2: 3rd harmonic (120.0 Hz) Sine with slight detuning
    this.overtoneOsc2 = this.ctx.createOscillator();
    this.overtoneOsc2.type = 'sine';
    this.overtoneOsc2.frequency.setValueAtTime(this.baseFreq2 * 3, t);
    this.overtoneOsc2.connect(this.overtoneGainNode);

    this.osc1.start(t);
    this.osc2.start(t);
    this.overtoneOsc1.start(t);
    this.overtoneOsc2.start(t);
  }

  public stop(): void {
    if (!this.isStarted) return;
    this.isStarted = false;

    const t = this.ctx.currentTime;
    try {
      this.osc1?.stop(t);
      this.osc2?.stop(t);
      this.overtoneOsc1?.stop(t);
      this.overtoneOsc2?.stop(t);
    } catch {
      // Ignore if already stopped
    }

    this.osc1 = null;
    this.osc2 = null;
    this.overtoneOsc1 = null;
    this.overtoneOsc2 = null;
  }

  /**
   * Smoothly crossfades drone parameters over the specified duration.
   */
  public updateCue(cue: AudioCue, duration = 2.0): void {
    if (!this.isStarted) {
      this.start();
    }

    const t = this.ctx.currentTime;
    const endTime = t + duration;

    // Mood specific tuning
    let targetFreq1 = this.baseFreq1;
    let targetFreq2 = this.baseFreq2;
    let targetFilter = 160;
    let targetQ = 3.0;
    let targetDroneGain = 0.5 * cue.intensity;
    let targetOvertoneGain = 0.15 * (cue.intensity + cue.dissonance);

    switch (cue.mood) {
      case 'deep_drone':
        targetFreq1 = 40.0;
        targetFreq2 = 43.2;
        targetFilter = 150 + cue.intensity * 80;
        targetQ = 2.5;
        targetDroneGain = 0.55 * Math.max(cue.intensity, 0.2);
        targetOvertoneGain = 0.12 * cue.intensity;
        break;

      case 'quantum_flutter':
        // Widened detune and higher harmonic excitation
        targetFreq1 = 40.0 + cue.dissonance * 4.0;
        targetFreq2 = 45.6 + cue.dissonance * 8.0;
        targetFilter = 280 + cue.intensity * 180;
        targetQ = 4.5;
        targetDroneGain = 0.5 * Math.max(cue.intensity, 0.2);
        targetOvertoneGain = 0.25 * (cue.intensity + cue.dissonance);
        break;

      case 'subharmonic_pulse':
        // Deep resonant sub-rumble
        targetFreq1 = 38.0;
        targetFreq2 = 40.8;
        targetFilter = 120 + cue.subharmonicBass * 40;
        targetQ = 2.0;
        targetDroneGain = 0.65 * Math.max(cue.intensity, 0.3);
        targetOvertoneGain = 0.08 * cue.intensity;
        break;

      case 'caesura_silence':
        // The unrendered void gap - dead silence drop
        targetFilter = 0.001;
        targetDroneGain = 0.0;
        targetOvertoneGain = 0.0;
        break;

      case 'synaptic_alarm':
        // Dissonant detuning and high resonance
        targetFreq1 = 44.0 + cue.dissonance * 12.0;
        targetFreq2 = 52.4 + cue.dissonance * 16.0;
        targetFilter = 420 + cue.intensity * 250;
        targetQ = 6.0;
        targetDroneGain = 0.6 * Math.max(cue.intensity, 0.2);
        targetOvertoneGain = 0.32 * (cue.intensity + cue.dissonance);
        break;
    }

    // Apply smooth linear / exponential ramps
    this.rampFrequency(this.osc1?.frequency, targetFreq1, duration);
    this.rampFrequency(this.osc2?.frequency, targetFreq2, duration);
    this.rampFrequency(this.overtoneOsc1?.frequency, targetFreq1 * 2, duration);
    this.rampFrequency(this.overtoneOsc2?.frequency, targetFreq2 * 3, duration);

    // Filter cutoff ramp
    if (this.filterNode) {
      this.filterNode.frequency.cancelScheduledValues(t);
      this.filterNode.frequency.setValueAtTime(this.filterNode.frequency.value, t);
      if (cue.mood === 'caesura_silence') {
        // Sudden drop for Caesura pause (0.4s drop)
        this.filterNode.frequency.linearRampToValueAtTime(0, t + Math.min(0.4, duration));
      } else {
        this.filterNode.frequency.linearRampToValueAtTime(targetFilter, endTime);
      }

      this.filterNode.Q.cancelScheduledValues(t);
      this.filterNode.Q.setValueAtTime(this.filterNode.Q.value, t);
      this.filterNode.Q.linearRampToValueAtTime(targetQ, endTime);
    }

    // Gain ramps
    this.rampGain(this.droneGainNode.gain, targetDroneGain, duration);
    this.rampGain(this.overtoneGainNode.gain, targetOvertoneGain, duration);
  }

  /**
   * Direct filter sweep for transitions.
   */
  public sweepFilterCutoff(targetHz: number, duration: number): void {
    const t = this.ctx.currentTime;
    this.filterNode.frequency.cancelScheduledValues(t);
    this.filterNode.frequency.setValueAtTime(this.filterNode.frequency.value, t);
    this.filterNode.frequency.linearRampToValueAtTime(Math.max(0, targetHz), t + duration);
  }

  private rampFrequency(param: AudioParam | undefined, target: number, duration: number): void {
    if (!param) return;
    const t = this.ctx.currentTime;
    param.cancelScheduledValues(t);
    param.setValueAtTime(param.value, t);
    param.linearRampToValueAtTime(target, t + duration);
  }

  private rampGain(param: AudioParam, target: number, duration: number): void {
    const t = this.ctx.currentTime;
    param.cancelScheduledValues(t);
    param.setValueAtTime(param.value, t);
    param.linearRampToValueAtTime(Math.max(0, target), t + duration);
  }
}
